import React, { useContext, useState } from 'react';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import SuperLinkDetails from './SuperLinkDetails';
import SuperLinkEdit from './SuperLinkEdit';
import SuperLinkAssessmentsExpirationUpdate from './SuperLinkAssessmentsExpirationUpdate';
import { AppConstants } from '../../../shared/Constants';
import DEFAULT_PAGE_SIZE from '../../../constant/UserProfileConst';
import { Loading } from '../../../components/Loading';
import { getObjectFromQueryParam } from '../../../utils/UrlUtil';
import Boundary from '../../../components/ErrorBoundary';
import ArrayUtil from '../../../utils/ArrayUtil';
import { AccountContext } from '../../../components/AccountContext';
import SuperlinkAssessmentListContainer from './super-link-assesments/SuperlinkAssessmentListContainer';
import SuperLinkAssessmentsResetStatusExpirationUpdate from './SuperLinkAssessmentsResetStatusExpirationUpdate';
import { useSelector } from 'react-redux';
import useCustomFunctionalityCheck from '../../../hooks/useCustomFunctionalityCheck';

const SUPERLINK_QUERY = gql`
  query superLinkDetailQuery($accountId: ID!, $id: ID!) {
    account(id: $accountId) {
      id
      superLink(id: $id) {
        id
        name
        description
        notes
        reports
        notificationEmails
        sendRespondentReport
        sendRespondentCcInterestsReport
        sendRespondentCcPersonalityReport
        ccInterestsMinimumMatchPercentage
        ccPersonalityMinimumMatchPercentage
        printFriendlyReport
        totalAssessment
        totalFinalStateAssessment
        locale
        activationOptions {
          enabled
          activationType
          numberOfAssessmentsToDisableAfter
        }
        publicURL
        assessmentTemplate {
          id
          title
        }
        targetJob {
          id
          name
        }
        createdOn
        updatedOn
      }
    }
    locales: activeLocales
  }
`;

const SuperLinkDetailGraphql = (props) => {
  const currentAccount = useContext(AccountContext);
  const { superLinkId } = useParams();

  const variables = {
    id: superLinkId,
    accountId: currentAccount,
    filterNew: {
      status: 'NEW',
    },
    filterInvited: {
      status: 'INVITED',
    },
    filterStated: {
      status: 'STARTED',
    },
  };

  return (
    <Query query={SUPERLINK_QUERY} variables={variables}>
      {({ data, loading, error }) => {
        if (error) return `Error! ${error.message}`;

        if (loading || !data.account.superLink) {
          return <Loading />;
        }
        const superLink = data && data.account ? data.account.superLink : {};
        const locales = data && data.locales ? data.locales : [];
        return <SuperLinkDetailContainer superLink={superLink} loading={loading} locales={locales} {...props} />;
      }}
    </Query>
  );
};

const SuperLinkDetailContainer = ({ superLink, locales }) => {
  const { search } = useLocation();
  const queryObject = getObjectFromQueryParam(search);

  const DEFAULT_EDIT_FLAGS_STATE = {
    core: false,
  };

  const assessmentsFilters = {
    status: null,
    query: null,
    from: queryObject ? queryObject.from : null,
    to: queryObject ? queryObject.to : null,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const { getReportsToAutomaticallyGenerate } = useCustomFunctionalityCheck();
  const reportsToAutomaticallyGenerate = getReportsToAutomaticallyGenerate();

  const [editFlags, setEditFlags] = useState(DEFAULT_EDIT_FLAGS_STATE);

  const toggleEdit = (section) => {
    const updatedEditFlags = { ...editFlags };
    updatedEditFlags[section] = !updatedEditFlags[section];

    setEditFlags(updatedEditFlags);
  };

  const viewerLang = superLink && superLink.locale ? superLink.locale.tag : null;

  const printerFriendlyReport = false;
  const initialValues = {
    locale: viewerLang,
    'start-date': assessmentsFilters.from ? assessmentsFilters.from : null,
    'end-date': assessmentsFilters.to ? assessmentsFilters.to : null,
    printerFriendlyReport,
  };

  const superlink_langs = [];
  locales.map((lang) => superlink_langs.push(lang));
  const availabel_lang = superlink_langs.indexOf(viewerLang);
  const generate_report_lang_default = availabel_lang !== -1 ? viewerLang : AppConstants.DEFAULT_LANG;

  let listReport = '';
  if (ArrayUtil.isNotEmpty(superLink.reports)) {
    superLink.reports.forEach((list, i) => {
      reportsToAutomaticallyGenerate.forEach((report) => {
        if (list === report.value) {
          if (i !== 0) {
            listReport += ', ';
          }
          listReport = listReport.concat(report.label);
        }
      });
    });
  }

  const getJobProfiles = superLink.availableJobProfiles;
  let listJobProfiles = [];
  if (getJobProfiles) {
    listJobProfiles = getJobProfiles.map((job) => ({ value: job.id, text: job.name }));
  }

  if (superLink.targetJob) {
    listJobProfiles
      .filter((job) => job.value === superLink.targetJob.id)
      .forEach(() => {
        initialValues['job-profile-id'] = {
          value: superLink.targetJob.id,
        };
      });
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <SuperLink
            editFlags={editFlags}
            superLink={superLink}
            toggleEdit={toggleEdit}
            assessmentsFilters={assessmentsFilters}
            printerFriendlyReport={printerFriendlyReport}
            initialValues={initialValues}
            listReport={listReport}
            activeLocales={locales}
            listJobProfiles={listJobProfiles}
            generateReportLangDefault={generate_report_lang_default}
          />
          <br />
          <Boundary>
            <SuperlinkAssessmentListContainer />
          </Boundary>
        </div>
      </div>
    </div>
  );
};

SuperLinkDetailContainer.propTypes = {
  queryObject: PropTypes.objectOf(PropTypes.any).isRequired,
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  locales: PropTypes.arrayOf(PropTypes.any).isRequired,
};

const SuperLink = ({
  editFlags,
  superLink,
  assessmentsFilters,
  printerFriendlyReport,
  initialValues,
  toggleEdit,
  listReport,
  activeLocales,
  listJobProfiles,
  generateReportLangDefault,
}) => {
  const currentAccount = useContext(AccountContext);
  const { translatedTags: languageList } = useSelector((state) => state.i18n);

  if (editFlags.core) {
    return <SuperLinkEdit superLink={superLink} toggleEdit={toggleEdit} languageList={languageList} />;
  }

  if (editFlags.updateExpiration) {
    return <SuperLinkAssessmentsExpirationUpdate superLink={superLink} toggleEdit={toggleEdit} />;
  }

  if (editFlags.resetStatusUpdateExpiration) {
    return <SuperLinkAssessmentsResetStatusExpirationUpdate superLink={superLink} toggleEdit={toggleEdit} />;
  }

  return (
    <SuperLinkDetails
      assessmentsFilters={assessmentsFilters}
      printerFriendlyReport={printerFriendlyReport}
      initialValues={initialValues}
      superLink={superLink}
      toggleEdit={toggleEdit}
      listReport={listReport}
      activeLocales={activeLocales}
      listJobProfiles={listJobProfiles}
      languageList={languageList}
      currentAccount={currentAccount}
      generateReportLangDefault={generateReportLangDefault}
    />
  );
};

SuperLink.propTypes = {
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  editFlags: PropTypes.objectOf(PropTypes.any).isRequired,
  assessmentsFilters: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  toggleEdit: PropTypes.func.isRequired,
  listReport: PropTypes.string.isRequired,
  printerFriendlyReport: PropTypes.bool.isRequired,
  initialValues: PropTypes.objectOf(PropTypes.any).isRequired,
  activeLocales: PropTypes.objectOf(PropTypes.any).isRequired,
  listJobProfiles: PropTypes.objectOf(PropTypes.any).isRequired,
  generateReportLangDefault: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default SuperLinkDetailGraphql;
